import React, { useState, useCallback } from 'react';
import {
  Form,
  Upload,
  Button,
  Space,
  Modal,
  Tag,
  Spin,
  Divider,
  Alert,
  message,
} from 'antd';
import {
  UploadOutlined,
  GlobalOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useSocketContext } from '../../../sockets/SocketContext';
import {
  globalSearchStart,
  globalSearchReset,
} from '../../../store/globalSearchSlice';
import { GlobalSearchResultReport } from './GlobalSearchReport';
import ImageCropper from '../ext/ImageCropper';
import StickySearchPanel from '../../common/StickySearchPanel';
import {
  preprocessImage,
  getCompressedBase64,
  MAX_INPUT_SIZE_BYTES,
} from '../../../utils/imagePreprocess';
import {
  GLOBAL_SEARCH_PIPELINE_LABELS,
  hasGlobalSearchReportContent,
} from '../../../utils/globalSearchUi';

const GlobalSearchTab = () => {
  const dispatch = useDispatch();
  const { createGlobalImageSearchTask } = useSocketContext();

  const { currentTaskId, loading, pipelineStage, result, error } = useSelector(
    (s) => s.globalSearch
  );

  const [previewSrc, setPreviewSrc] = useState(null);
  const [originalPreviewSrc, setOriginalPreviewSrc] = useState(null);
  const [cropVisible, setCropVisible] = useState(false);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [cropConfig, setCropConfig] = useState(null);
  const [fileName, setFileName] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const resetFile = useCallback(() => {
    setPreviewSrc(null);
    setOriginalPreviewSrc(null);
    setCroppedBlob(null);
    setCropConfig(null);
    setFileName(null);
  }, []);

  const handleFileChange = async ({ fileList }) => {
    if (!fileList.length) {
      resetFile();
      return;
    }
    let file = fileList[fileList.length - 1].originFileObj;
    const originalSize = file.size;
    try {
      file = await preprocessImage(file);
      if (file.size < originalSize) {
        message.info('Изображение сжато до допустимого размера');
      }
    } catch (err) {
      message.error(err?.message || 'Ошибка обработки изображения');
      resetFile();
      return;
    }
    setFileName(file.name);
    setCroppedBlob(null);
    setCropConfig(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataURL = e.target.result;
      setOriginalPreviewSrc(dataURL);
      setPreviewSrc(dataURL);
      setCropVisible(true);
    };
    reader.readAsDataURL(file);
  };

  const taskInProgress = submitting || loading;

  const runSearch = async () => {
    if (!previewSrc && !croppedBlob) {
      message.error('Сначала выберите изображение');
      return;
    }
    if (taskInProgress) {
      return;
    }

    setSubmitting(true);
    dispatch(globalSearchReset());

    try {
      const base64 = await getCompressedBase64(croppedBlob || previewSrc);
      const dataUrl = `data:image/jpeg;base64,${base64}`;

      createGlobalImageSearchTask(dataUrl, (err, task) => {
        setSubmitting(false);
        if (err || !task?.id) {
          Modal.error({
            title: 'Не удалось создать задачу',
            content: typeof err === 'string' ? err : 'Неизвестная ошибка',
          });
          return;
        }
        dispatch(
          globalSearchStart({ taskId: task.id, fileName, imageBase64: base64 }),
        );
      });
    } catch (err) {
      setSubmitting(false);
      message.error(err?.message || 'Ошибка сжатия изображения');
    }
  };

  const stageTag =
    pipelineStage && GLOBAL_SEARCH_PIPELINE_LABELS[pipelineStage] ? (
      <Tag color="processing">{GLOBAL_SEARCH_PIPELINE_LABELS[pipelineStage]}</Tag>
    ) : loading || submitting ? (
      <Tag>В работе</Tag>
    ) : null;

  const previewImage = previewSrc;
  const imageQuery = fileName || 'Глобальный поиск по изображению';

  const compactContent = (
    <>
      {previewImage ? (
        <img
          src={previewImage}
          alt="Выбранное изображение"
          className="sticky-search-panel__thumbnail"
          title="Нажмите «К форме», чтобы вернуться к форме"
        />
      ) : null}
      <div className="sticky-search-panel__compact-body">
        {fileName ? (
          <span className="sticky-search-panel__query-text">
            <SearchOutlined style={{ marginRight: 6 }} />
            {fileName}
          </span>
        ) : (
          <span className="sticky-search-panel__query-text" style={{ opacity: 0.65 }}>
            <GlobalOutlined style={{ marginRight: 6 }} />
            Выберите изображение
          </span>
        )}
        <div className="sticky-search-panel__query-meta">
          {(loading || submitting) && <Tag color="processing">Поиск…</Tag>}
          {pipelineStage && GLOBAL_SEARCH_PIPELINE_LABELS[pipelineStage] ? (
            <Tag color="blue">{GLOBAL_SEARCH_PIPELINE_LABELS[pipelineStage]}</Tag>
          ) : null}
          {error ? <Tag color="error">Ошибка</Tag> : null}
        </div>
      </div>
    </>
  );

  return (
    <div style={{ width: '100%', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <Form
          layout="inline"
          onFinish={runSearch}
          style={{ flexWrap: 'wrap', gap: 16, marginBottom: 16, flex: 1 }}
        >
          <Form.Item label="Изображение">
            <Upload
              disabled={taskInProgress}
              beforeUpload={(file) => {
                if (file.size > MAX_INPUT_SIZE_BYTES) {
                  message.error(`Файл слишком большой (макс. ${MAX_INPUT_SIZE_BYTES / 1024 / 1024} MB)`);
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={handleFileChange}
              onRemove={resetFile}
              maxCount={1}
              accept="image/*"
              listType="picture-card"
              showUploadList={false}
            >
              {!previewSrc && (
                <div>
                  <UploadOutlined style={{ fontSize: 24 }} />
                  <div style={{ marginTop: 8 }}>Выбрать</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          {previewSrc ? (
            <div style={{ position: 'relative', display: 'inline-block', marginRight: 16 }}>
              <img
                src={previewSrc}
                alt={imageQuery}
                onClick={() => {
                  if (!taskInProgress) setCropVisible(true);
                }}
                style={{
                  maxWidth: 100,
                  maxHeight: 100,
                  objectFit: 'contain',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  cursor: taskInProgress ? 'default' : 'pointer',
                  display: 'block',
                }}
              />
              <CloseCircleOutlined
                style={{
                  fontSize: 22,
                  color: taskInProgress ? 'rgba(0,0,0,0.25)' : '#ff4d4f',
                  cursor: taskInProgress ? 'not-allowed' : 'pointer',
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  background: 'var(--theme-surface)',
                  borderRadius: '50%',
                  boxShadow: '0 0 2px #ccc',
                  pointerEvents: taskInProgress ? 'none' : 'auto',
                }}
                title="Убрать изображение"
                onClick={(e) => {
                  e.stopPropagation();
                  if (taskInProgress) return;
                  resetFile();
                }}
              />
            </div>
          ) : null}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<FileSearchOutlined />}
              loading={taskInProgress}
              disabled={!previewSrc && !croppedBlob}
            >
              Найти
            </Button>
          </Form.Item>
        </Form>
      </div>

      {(submitting || loading || stageTag || error) && (
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            {stageTag}
            {(loading || submitting) && <Spin size="small" />}
          </Space>
          {error && (
            <Alert type="error" message={error} style={{ marginTop: 12 }} showIcon />
          )}
        </div>
      )}

      {currentTaskId && hasGlobalSearchReportContent(result) ? (
        <div className="global-search-tab__live-report">
          <Divider orientation="left">Результат</Divider>
          <GlobalSearchResultReport payload={result} />
        </div>
      ) : null}

      <StickySearchPanel compactContent={compactContent} threshold={120} offsetTop={12} overlayOnly />

      <Modal
        open={cropVisible}
        footer={null}
        onCancel={() => setCropVisible(false)}
        width="min(800px, calc(100vw - 32px))"
        centered
        destroyOnClose
      >
        {originalPreviewSrc && (
          <ImageCropper
            src={originalPreviewSrc}
            initialCrop={cropConfig}
            onComplete={(blob, cfg) => {
              setCroppedBlob(blob);
              setCropConfig(cfg);
              const r = new FileReader();
              r.onload = (e) => {
                setPreviewSrc(e.target.result);
              };
              r.readAsDataURL(blob);
              setCropVisible(false);
            }}
            onCancel={() => setCropVisible(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default GlobalSearchTab;
